import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card       from "@react/shared/ui/Card"
import Button     from "@react/shared/ui/Button"
import IconBadge  from "@react/shared/ui/IconBadge"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import { formatNumber, formatCoef, roundNumber, quantityOf } from "../helpers/formatting"
import { costCategoryVisual } from "@react/quotes/qline_service_blueprints/kinds"
import { hasLiveChildren } from "../helpers/treeMath"

import {
  parseCoef, parseCoefSigned, overheadCoefFor, policySaleCoefFor,
  composeK, saleCoefFromK, effectiveSaleCoefFor,
} from "../helpers/coefficients"

const ALL_CATEGORIES = ["labor", "equipment", "materials", "subcontracting", "other"]

const enabledCategoriesFor = account => {
  const list = Array.isArray(account?.enabled_cost_categories) ? account.enabled_cost_categories : []
  const filtered = list.filter(category => ALL_CATEGORIES.includes(category))
  return filtered.length > 0 ? filtered : ALL_CATEGORIES
}

const collectBuckets = (sections, categories) => {
  const buckets = {}
  categories.forEach(category => (buckets[category] = { sale: 0, cost: 0, hours: 0, count: 0 }))

  const walk = (node, factor) => {
    if (!node || node._destroy) return
    if (node.type === "service" && hasLiveChildren(node)) {
      node.children.forEach(child => walk(child, factor * (parseFloat(node.quantity) || 0)))
      return
    }
    if (node.type === "service") {
      const category = node.cost_category || "materials"
      const bucket   = buckets[category]
      if (!bucket) return
      const quantity = (parseFloat(node.quantity) || 0) * factor
      bucket.sale  += quantity * (parseFloat(node.unit_price)     || 0)
      bucket.cost  += quantity * (parseFloat(node.purchase_price) || 0)
      bucket.count += 1
      if (category === "labor") bucket.hours += quantity
      return
    }
    const liveChildren    = (node.children || []).filter(child => !child._destroy)
    const hasServiceChild = liveChildren.some(child => child.type === "service")
    const nextFactor      = hasServiceChild ? factor * quantityOf(node.quantity) : factor
    liveChildren.forEach(child => walk(child, nextFactor))
  }

  ;(sections || []).filter(section => !section._destroy).forEach(section => walk(section, 1))
  return buckets
}

const collectLines = sections => {
  const lines = []
  const walk = node => {
    if (!node || node._destroy) return
    if (node.type === "service" && !hasLiveChildren(node)) {
      lines.push({
        category: node.cost_category || "materials",
        purchase: parseFloat(node.purchase_price) || 0,
        sale:     parseFloat(node.unit_price)     || 0,
      })
      return
    }
    ;(node.children || []).forEach(walk)
  }
  ;(sections || []).forEach(walk)
  return lines
}

const marginTone = amount => (amount > 0 ? "text-emerald-600" : amount < 0 ? "text-rose-600" : "text-slate-500")

const MarginCell = ({ profit, base }) => {
  if (base <= 0 && profit === 0) return <span className="text-slate-300">—</span>
  const pct = base > 0 ? (profit / base) * 100 : 0
  return (
    <span className={`font-semibold ${marginTone(profit)}`}>
      {formatNumber(profit)}
      <span className="ml-1.5 text-[11px] font-medium opacity-80">({formatNumber(pct)} %)</span>
    </span>
  )
}

const MarginsTab = () => {
  const { state, dispatch } = useQuoteEditor()
  const symbol   = state.currencySymbol || "€"
  const account  = state.account || {}
  const categories = enabledCategoriesFor(account)

  const [targets, setTargets]     = useState({})
  const [fgTargets, setFgTargets] = useState({})
  const [kTargets, setKTargets]   = useState({})

  const forget = (map, category) => {
    if (map[category] === undefined) return map
    const next = { ...map }
    delete next[category]
    return next
  }

  // Sale coefficient and K say the same thing two ways: the one being typed
  // takes over and clears its twin.
  const setSale = (category, raw) => {
    setTargets(previous => ({ ...previous, [category]: raw }))
    setKTargets(previous => forget(previous, category))
  }
  const setK = (category, raw) => {
    setKTargets(previous => ({ ...previous, [category]: raw }))
    setTargets(previous => forget(previous, category))
  }
  // Correcting the overhead keeps the commercial margin and lets K rise. A K
  // just typed is frozen into a sale coefficient first, or the decision taken
  // would be wiped by the next correction.
  const setFg = (category, raw, saleCoef) => {
    const hadK = kTargets[category] !== undefined
    setFgTargets(previous => ({ ...previous, [category]: raw }))
    if (!hadK) return
    setTargets(previous => ({ ...previous, [category]: saleCoef > 0 ? formatCoef(saleCoef) : "" }))
    setKTargets(previous => forget(previous, category))
  }

  const buckets = useMemo(
    () => collectBuckets(state.sections, categories),
    [state.sections, categories],
  )

  const lines = useMemo(() => collectLines(state.sections), [state.sections])

  const appliedOverhead = category => overheadCoefFor(state, category)

  const overheadAmount = category => buckets[category].cost * appliedOverhead(category)
  const costPriceFor   = category => buckets[category].cost + overheadAmount(category)

  // The sale coefficient in force is read back from the prices; on an empty
  // quote, the one that will apply to the first line typed.
  const appliedSale = category => {
    const costPrice = costPriceFor(category)
    if (costPrice > 0) return buckets[category].sale / costPrice

    return effectiveSaleCoefFor(state, category) || 0
  }

  const netProfitFor = category => (buckets[category].sale - buckets[category].cost) - overheadAmount(category)

  const buildRow = category => {
    const bucket  = buckets[category]
    const hasCost = bucket.count > 0 && bucket.cost > 0

    const appliedSaleValue = appliedSale(category)
    const appliedFgValue   = appliedOverhead(category)
    const appliedFgStr     = formatCoef(appliedFgValue)

    const rawSale = targets[category]
    const rawFg   = fgTargets[category]
    const rawK    = kTargets[category]

    // An emptied field means "untouched", never zero.
    const typedSale = rawSale === undefined || rawSale === "" ? undefined : rawSale
    const typedFg   = rawFg   === undefined || rawFg   === "" ? undefined : rawFg
    const typedK    = rawK    === undefined || rawK    === "" ? undefined : rawK

    // Selling price = purchase price × (1 + overhead coef) × sale coef.
    const overheadCoef = typedFg !== undefined ? parseCoefSigned(typedFg) : appliedFgValue
    const saleCoef     = typedK    !== undefined ? saleCoefFromK(overheadCoef, parseCoef(typedK))
                       : typedSale !== undefined ? parseCoef(typedSale)
                       : appliedSaleValue

    // K: dry cost to selling price.
    const kValue = composeK(overheadCoef, saleCoef)

    // All three at the thousandth: the line must multiply out on screen.
    const fgInput   = rawFg   !== undefined ? rawFg   : appliedFgStr
    const saleInput = rawSale !== undefined ? rawSale : (saleCoef > 0 ? formatCoef(saleCoef) : "")
    const kInput    = rawK    !== undefined ? rawK    : (kValue   > 0 ? formatCoef(kValue)   : "")

    const targetDiffers = Math.abs(saleCoef    - appliedSaleValue) > 0.0001
    const fgDiffers     = Math.abs(overheadCoef - appliedFgValue)  > 0.0001

    // "By hand" is measured against the SAVED policy, never the displayed
    // coefficient, which is the average of the lines.
    const policy   = policySaleCoefFor(state, category)
    const affected = lines.filter(line => line.category === category && line.purchase > 0)
    const wouldChange = affected.filter(line =>
      Math.abs(roundNumber(line.purchase * (1 + overheadCoef) * saleCoef) - line.sale) > 0.005)
    const offPolicy = policy == null ? [] : wouldChange.filter(line =>
      Math.abs(roundNumber(line.purchase * (1 + appliedFgValue) * policy) - line.sale) > 0.005)

    return {
      category,
      bucket,
      hasCost,
      fgInput,
      saleInput,
      kInput,
      fgDiffers,
      targetDiffers,
      saleCoef,
      overheadCoef,
      kValue,
      pending: (targetDiffers || fgDiffers) && (saleCoef > 0 || fgDiffers),
      wouldChange: wouldChange.length,
      offPolicy:   offPolicy.length,
      profit:  netProfitFor(category),
    }
  }

  const rows           = categories.map(buildRow)
  const pendingChanges = rows
    .filter(row => row.pending)
    .map(({ category, saleCoef, overheadCoef }) => ({ category, saleCoef, overheadCoef }))
  const editableCount  = rows.filter(row => row.hasCost).length

  const applyAll = () => {
    if (pendingChanges.length === 0) return
    dispatch({ type: "APPLY_CATEGORY_COEFS_BULK", payload: { changes: pendingChanges } })
    setTargets({})
    setFgTargets({})
    setKTargets({})
  }

  const totals = categories.reduce((acc, category) => {
    const bucket = buckets[category]
    acc.sale  += bucket.sale
    acc.cost  += bucket.cost
    acc.count += bucket.count
    acc.hours += bucket.hours
    acc.overhead += overheadAmount(category)
    return acc
  }, { sale: 0, cost: 0, count: 0, hours: 0, overhead: 0 })

  const netTotal       = (totals.sale - totals.cost) - totals.overhead
  const netPctTotal    = totals.sale > 0 ? (netTotal / totals.sale) * 100 : 0
  const grossTotal     = totals.sale - totals.cost
  const fgCoefTotal    = totals.cost > 0 ? totals.overhead / totals.cost : 0
  // The global sale coefficient bears on the cost price, overhead included.
  const costPriceTotal = totals.cost + totals.overhead
  const saleCoefTotal  = costPriceTotal > 0 ? totals.sale / costPriceTotal : 0
  const kTotal         = totals.cost > 0 ? totals.sale / totals.cost : 0

  const pendingRows   = rows.filter(row => row.pending)
  const changedLines  = pendingRows.reduce((sum, row) => sum + row.wouldChange, 0)
  const manualLines   = pendingRows.reduce((sum, row) => sum + row.offPolicy, 0)

  const headerProfit = netTotal
  const headerPct    = netPctTotal
  const headerLabel  = I18n.t("quotes.cost_breakdown_table.net_margin_summary")

  const HEAD = "py-2 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap"
  const NUM  = "py-2.5 px-2 text-right tabular-nums whitespace-nowrap align-middle"
  const inputCls = "w-[72px] h-8 px-2 text-right tabular-nums text-[13px] rounded-md border border-slate-300 bg-white shadow-sm outline-none hover:border-slate-400 focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20"

  const noCostCell = (
    <span className="text-slate-300" title={I18n.t("quotes.cost_breakdown_table.no_cost_tooltip")}>—</span>
  )

  // The field stays editable with no line: the coefficient must be decidable
  // before pricing, and lines typed afterwards inherit it.
  const coefCell = ({ value, pending, placeholder, tooltip, onChange, strong }) => (
    <td className="py-2.5 px-2 whitespace-nowrap align-middle">
      <input
        type        = "text"
        inputMode   = "decimal"
        placeholder = {placeholder}
        value       = {value}
        onChange    = {event => onChange(event.target.value)}
        className   = {`${inputCls} ${strong ? "font-semibold" : ""} ${pending ? "border-brand-500 ring-[3px] ring-brand-500/20" : ""}`}
        title       = {tooltip}
      />
    </td>
  )

  return (
    <div className="space-y-4">
      <Card padding="lg" className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <FontAwesomeIcon icon="chart-pie" className="text-slate-400" />
            {I18n.t("quotes.cost_breakdown_table.title")}
          </div>
          <div className="text-[13px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-1.5">
            {headerLabel} :
            <strong className={`ml-1.5 ${headerPct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {formatNumber(headerProfit)} {symbol}
              <span className="ml-1.5 text-[12px] font-medium">({formatNumber(headerPct)} %)</span>
            </strong>
          </div>
        </div>

        {totals.count === 0 ? (
          <div className="flex items-start gap-2 text-[13px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <FontAwesomeIcon icon="circle-info" className="mt-0.5 text-slate-400" />
            <span>{I18n.t("quotes.builder.margins_tab.empty_banner")}</span>
          </div>
        ) : editableCount === 0 && (
          <div className="flex items-start gap-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            <FontAwesomeIcon icon="circle-info" className="mt-0.5 text-amber-500" />
            <span>{I18n.t("quotes.cost_breakdown_table.no_cost_banner")}</span>
          </div>
        )}

        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-slate-200">
                <th className={`${HEAD} text-left`}>{I18n.t("quotes.cost_breakdown_table.category")}</th>
                <th className={`${HEAD} text-right`}>{I18n.t("quotes.cost_breakdown_table.lines")}</th>
                <th className={`${HEAD} text-right`}>{I18n.t("quotes.cost_breakdown_table.hours")}</th>
                <th className={`${HEAD} text-right`}>{I18n.t("quotes.cost_breakdown_table.purchase_dry")} ({symbol})</th>
                <th className={`${HEAD} text-left`}>{I18n.t("quotes.cost_breakdown_table.overhead_coef")}</th>
                <th className={`${HEAD} text-left`}>{I18n.t("quotes.cost_breakdown_table.sale_coef")}</th>
                <th className={`${HEAD} text-left`}>{I18n.t("quotes.cost_breakdown_table.k_coef")}</th>
                <th className={`${HEAD} text-right`}>{I18n.t("quotes.cost_breakdown_table.sale")} ({symbol})</th>
                <th className={`${HEAD} text-right`}>{I18n.t("quotes.cost_breakdown_table.margin")} ({symbol})</th>
                <th className={`${HEAD} text-right`}>{I18n.t("quotes.cost_breakdown_table.overhead")} ({symbol})</th>
                <th className={`${HEAD} text-right`}>{I18n.t("quotes.cost_breakdown_table.net_profit")} ({symbol})</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const { category, bucket } = row
                const visual = costCategoryVisual(category)

                return (
                  <tr key={category} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                    <td className="py-2.5 px-2 align-middle">
                      <div className="flex items-center gap-2.5 whitespace-nowrap">
                        <IconBadge tone={visual.tone} size="sm">
                          <FontAwesomeIcon icon={visual.icon} />
                        </IconBadge>
                        <span className="font-medium text-slate-900">{I18n.t(`quotes.new.cost_category.${category}`)}</span>
                      </div>
                    </td>
                    <td className={`${NUM} text-slate-500`}>{bucket.count === 0 ? <span className="text-slate-300">—</span> : bucket.count}</td>
                    <td className={`${NUM} text-slate-500`}>{bucket.hours > 0 ? `${formatNumber(bucket.hours)} h` : <span className="text-slate-300">—</span>}</td>
                    <td className={`${NUM} text-slate-600`}>{bucket.cost > 0 ? formatNumber(bucket.cost) : <span className="text-slate-300">—</span>}</td>
                    {coefCell({
                      value:       row.fgInput,
                      pending:     row.fgDiffers,
                      placeholder: "0,15",
                      tooltip:     I18n.t("quotes.builder.margins_tab.overhead_coef_tooltip"),
                      onChange:    value => setFg(row.category, value, row.saleCoef),
                    })}
                    {coefCell({
                      value:       row.saleInput,
                      pending:     row.targetDiffers,
                      placeholder: "1,30",
                      tooltip:     I18n.t("quotes.cost_breakdown_table.sale_coef_tooltip"),
                      onChange:    value => setSale(row.category, value),
                    })}
                    {coefCell({
                      value:       row.kInput,
                      pending:     row.targetDiffers || row.fgDiffers,
                      placeholder: "2,00",
                      strong:      true,
                      tooltip:     I18n.t("quotes.cost_breakdown_table.k_coef_tooltip"),
                      onChange:    value => setK(row.category, value),
                    })}
                    <td className={`${NUM} text-slate-900 font-medium`}>{bucket.sale > 0 ? formatNumber(bucket.sale) : <span className="text-slate-300">—</span>}</td>
                    <td className={NUM}>{bucket.sale > 0 || bucket.cost > 0 ? <MarginCell profit={bucket.sale - bucket.cost} base={bucket.sale} /> : <span className="text-slate-300">—</span>}</td>
                    <td className={`${NUM} text-slate-600`}>{bucket.cost > 0 ? formatNumber(overheadAmount(category)) : <span className="text-slate-300">—</span>}</td>
                    <td className={NUM}>{bucket.sale > 0 || bucket.cost > 0 ? <MarginCell profit={row.profit} base={bucket.sale} /> : <span className="text-slate-300">—</span>}</td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 bg-slate-50/60 font-semibold">
                <td className="py-3 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-700">{I18n.t("quotes.cost_breakdown_table.total")}</td>
                <td className={`${NUM} text-slate-700`}>{totals.count || <span className="text-slate-300">—</span>}</td>
                <td className={`${NUM} text-slate-700`}>{totals.hours > 0 ? `${formatNumber(totals.hours)} h` : <span className="text-slate-300">—</span>}</td>
                <td className={`${NUM} text-slate-700`}>{formatNumber(totals.cost)}</td>
                <td className={`${NUM} text-slate-700`}>{fgCoefTotal > 0 ? formatNumber(fgCoefTotal) : <span className="text-slate-300">—</span>}</td>
                <td className={`${NUM} text-slate-700`}>{saleCoefTotal > 0 ? `×${formatNumber(saleCoefTotal)}` : <span className="text-slate-300">—</span>}</td>
                <td className={`${NUM} text-slate-900 font-bold`}>{kTotal > 0 ? `×${formatCoef(kTotal)}` : <span className="text-slate-300">—</span>}</td>
                <td className={`${NUM} text-slate-900`}>{formatNumber(totals.sale)}</td>
                <td className={NUM}><MarginCell profit={grossTotal} base={totals.sale} /></td>
                <td className={`${NUM} text-slate-700`}>{totals.overhead > 0 ? formatNumber(totals.overhead) : <span className="text-slate-300">—</span>}</td>
                <td className={NUM}><MarginCell profit={netTotal} base={totals.sale} /></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
          <div className="max-w-[62ch] space-y-1">
            <p className="m-0 text-[12px] text-slate-500 font-medium">
              {I18n.t("quotes.builder.margins_tab.k_formula")}
            </p>
            <p className="m-0 text-[12px] text-slate-400">{I18n.t("quotes.builder.margins_tab.hint")}</p>
            {manualLines > 0 && (
              <p className="m-0 flex items-start gap-1.5 text-[12px] text-amber-700">
                <FontAwesomeIcon icon="triangle-exclamation" className="mt-0.5 text-amber-500" />
                <span>{I18n.t("quotes.builder.margins_tab.will_overwrite", {
                  count:  changedLines,
                  manual: manualLines,
                })}</span>
              </p>
            )}
          </div>
          <Button
            variant  = "primary"
            size     = "sm"
            disabled = {pendingChanges.length === 0}
            onClick  = {applyAll}
            title    = {I18n.t("quotes.cost_breakdown_table.apply_all_tooltip")}
          >
            {pendingChanges.length > 0
              ? I18n.t("quotes.cost_breakdown_table.apply_all_count", { count: pendingChanges.length })
              : I18n.t("quotes.cost_breakdown_table.apply_all")}
          </Button>
        </div>
      </Card>
    </div>
  )
}

export default MarginsTab
